/**
 * Blog post: Goethe B1 Guide – Struktur, Module & Strategien zum Bestehen.
 * Content: 4-Module grid, Goethe vs. telc B1, Redemittel link, 3 Tipps.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function GoetheB1ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Dein Fahrplan zum Goethe-Zertifikat B1 – Präsentiert vom logami.app Expertenteam.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Das <strong>Goethe-Zertifikat B1</strong> ist weltweit anerkannt und beweist, dass du die <strong>German Language</strong> im Alltag sicher beherrscht. Ob für den Job oder das Studium – eine gute <strong>Preparation</strong> ist der Schlüssel, um die <strong>B1 Exam</strong> erfolgreich zu <strong>ace</strong>.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Die Struktur: 4 Module zum Erfolg
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Im Gegensatz zu manch anderen Prüfungen besteht das <strong>Goethe-Zertifikat B1</strong> aus vier eigenständigen Modulen. Du kannst diese <strong>Modules</strong> einzeln oder zusammen ablegen.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6">
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space font-bold text-black mb-2">Lesen</p>
          <p className="text-sm text-black/70 leading-relaxed">5 Teile, 65 Min. Verstehe Blogbeiträge, Anzeigen &amp; formale Briefe.</p>
        </div>
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space font-bold text-black mb-2">Hören</p>
          <p className="text-sm text-black/70 leading-relaxed">4 Teile, 40 Min. Verstehe Durchsagen, Gespräche &amp; Vorträge.</p>
        </div>
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space font-bold text-black mb-2">Schreiben</p>
          <p className="text-sm text-black/70 leading-relaxed">3 Aufgaben, 60 Min. Verfasse E-Mails &amp; Forenbeiträge.</p>
        </div>
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space font-bold text-black mb-2">Sprechen</p>
          <p className="text-sm text-black/70 leading-relaxed">3 Teile, 15 Min. Plane etwas mit einem Partner &amp; halte einen Vortrag.</p>
        </div>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Ein intensives <strong>Exam Training</strong> mit einem <strong>Model Test</strong> hilft dir, dich an das Zeitlimit zu gewöhnen.
      </p>

      <div className="my-10 p-5 sm:p-6 rounded-2xl border-2 border-amber-200 bg-amber-50/80">
        <h2 className="font-space text-xl font-bold text-black mb-4">
          Goethe B1 vs. telc B1: Was ist der Unterschied?
        </h2>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Obwohl beide Prüfungen auf dem gleichen Niveau liegen, gibt es wichtige Unterschiede für dein <strong>Self Study</strong>:
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed ml-2">
          <li><strong>Modulbauweise:</strong> Bei Goethe kannst du Module einzeln wiederholen; bei telc ist die Prüfung oft als Ganzes (Schriftlich/Mündlich) gekoppelt.</li>
          <li><strong>Themen:</strong> Das <strong>Goethe B1</strong> Zertifikat ist oft etwas akademischer, während telc stärker auf den Berufsalltag fokussiert.</li>
          <li><strong>Sprechen:</strong> Bei Goethe hältst du eine kurze Präsentation, bei telc löst du eher eine gemeinsame Aufgabe.</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Deine Geheimwaffe: Redemittel &amp; Practice Material
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Um im Teil <strong>Writing &amp; Speaking</strong> zu glänzen, brauchst du feste Strukturen. Unsere <strong>Study Guide</strong>-Materialien bieten dir eine riesige Auswahl an Formulierungen.
      </p>
      <div className="my-10 p-6 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <h3 className="font-space text-lg font-bold text-black mb-3">Kostenlose Redemittel &amp; Vorlagen</h3>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Lade dir jetzt unser kostenloses <strong>PDF</strong> mit den wichtigsten Redemitteln für <strong>Goethe B1</strong> herunter! So meisterst du jeden <strong>Sample Set</strong> mühelos.
        </p>
        <Link
          to={`${blogBase}/goethe-b1-redemittel-schreiben`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt Redemittel (PDF) sichern
        </Link>
        <p className="mt-3 text-sm text-black/60">
          Inklusive <strong>Ace your B1</strong>-Tipps und <strong>Exercises</strong> für zu Hause.
        </p>
      </div>
      <SprechenSamplesCallout provider="goethe" level="b1" />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        3 Tipps, um dein Zertifikat B1 zu sichern
      </h2>
      <ol className="list-decimal list-inside space-y-3 text-base md:text-lg text-black/70 leading-relaxed mb-10 ml-2">
        <li><strong>Modelltests nutzen:</strong> Arbeite mit einem <strong>Sample Set</strong> unter realen Zeitbedingungen.</li>
        <li><strong>Hörverstehen im Alltag:</strong> Nutze <strong>Listening &amp; Reading</strong>-Übungen täglich, um dein Ohr an verschiedene Akzente zu gewöhnen.</li>
        <li><strong>Strukturierte E-Mails:</strong> Lerne für das <strong>Reading &amp; Writing</strong>-Modul genau, wie man eine Einleitung und einen Schluss formuliert.</li>
      </ol>

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Bereit für die Goethe B1 Prüfung?
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Starte dein <strong>B1 Exam Training</strong> jetzt mit logami.app. Wir bieten dir <strong>Online</strong>-Simulatoren, die sich wie die echte Prüfung anfühlen.
        </p>
        <Link
          to={`/${lang}/dashboard`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt kostenlos Mock Exam starten
        </Link>
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        Keywords: Goethe-Zertifikat B1 exam, B1 Goethe, practice materials, reading writing speaking, model tests, German language proficiency.
      </p>
      <RelatedResources provider="goethe" level="b1" variant="exam-guide" />

    </article>
  );
}
